import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import ReactFlow, {
  Background,
  Controls,
  ReactFlowProvider,
  useNodesState,
  useEdgesState,
  useReactFlow,
  MarkerType,
  getRectOfNodes,
  getTransformForBounds,
} from "reactflow";
import "reactflow/dist/style.css";
import { toPng } from "html-to-image";
import api, { formatApiError } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { ShareButton } from "@/components/ShareButton";
import { FolderSelect } from "@/components/FolderSelect";
import { MindMapContext, MindNode } from "@/components/MindMapNode";
import { mindmapToMarkdown, outlineToMindmap } from "@/lib/mindmapConvert";
import { emptyCells } from "@/lib/sheetEngine";
import { AiImproveModal } from "@/components/AiImproveModal";
import { useTheme } from "@/context/ThemeContext";
import { PAGE, BTN_PRIMARY, ICON_BTN } from "@/lib/styles";
import { ArrowLeft, Save, ZoomIn, ZoomOut, Maximize, X, Share2, FilePlus, FileText, Sparkles, Baby, Undo2, Redo2 } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { es } from "date-fns/locale";

let idSeq = 1;
const newId = () => `n${Date.now()}_${idSeq++}`;
const nowIso = () => new Date().toISOString();
const nodeTypes = { mindnode: MindNode };
const edgeStyle = { stroke: "#6366f1", strokeWidth: 2 };
const marker = { type: MarkerType.ArrowClosed, color: "#6366f1" };

function Editor({ id }) {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { zoomIn, zoomOut, fitView } = useReactFlow();
  const [title, setTitle] = useState("");
  const [folderId, setFolderId] = useState("");
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [saving, setSaving] = useState(false);
  const [historyFor, setHistoryFor] = useState(null);
  const [improveOpen, setImproveOpen] = useState(false);
  const [kids, setKids] = useState(false);
  const loaded = useRef(false);

  // --- Historial Deshacer/Rehacer ---
  const nodesRef = useRef([]);
  const edgesRef = useRef([]);
  useEffect(() => { nodesRef.current = nodes; }, [nodes]);
  useEffect(() => { edgesRef.current = edges; }, [edges]);
  const pastRef = useRef([]);
  const futureRef = useRef([]);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const syncHist = useCallback(() => { setCanUndo(pastRef.current.length > 0); setCanRedo(futureRef.current.length > 0); }, []);
  const snapshot = useCallback(() => {
    pastRef.current.push({ nodes: nodesRef.current, edges: edgesRef.current });
    if (pastRef.current.length > 60) pastRef.current.shift();
    futureRef.current = [];
    syncHist();
  }, [syncHist]);
  const undo = useCallback(() => {
    if (!pastRef.current.length) return;
    futureRef.current.push({ nodes: nodesRef.current, edges: edgesRef.current });
    const prev = pastRef.current.pop();
    setNodes(prev.nodes); setEdges(prev.edges); syncHist();
  }, [setNodes, setEdges, syncHist]);
  const redo = useCallback(() => {
    if (!futureRef.current.length) return;
    pastRef.current.push({ nodes: nodesRef.current, edges: edgesRef.current });
    const nx = futureRef.current.pop();
    setNodes(nx.nodes); setEdges(nx.edges); syncHist();
  }, [setNodes, setEdges, syncHist]);
  useEffect(() => {
    const onKey = (e) => {
      if (["INPUT", "TEXTAREA"].includes(e.target.tagName)) return;
      if ((e.ctrlKey || e.metaKey) && !e.shiftKey && e.key.toLowerCase() === "z") { e.preventDefault(); undo(); }
      else if ((e.ctrlKey || e.metaKey) && (e.key.toLowerCase() === "y" || (e.shiftKey && e.key.toLowerCase() === "z"))) { e.preventDefault(); redo(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [undo, redo]);

  useEffect(() => {
    let ignored = false;
    api
      .get(`/notes/${id}`)
      .then(({ data }) => {
        if (ignored) return;
        setTitle(data.title);
        setFolderId(data.folder_id || "");
        const mm = data.mindmap || {};
        setKids(!!mm.kids);
        if (mm.nodes && mm.nodes.length) {
          setNodes(mm.nodes);
          setEdges(mm.edges || []);
        } else {
          setNodes([{
            id: "root",
            type: "mindnode",
            position: { x: 0, y: 0 },
            data: { label: data.title || "Idea central", createdAt: nowIso(), updatedAt: nowIso(), history: [], isRoot: true },
          }]);
          setEdges([]);
        }
        loaded.current = true;
      })
      .catch((err) => {
        if (ignored) return;
        toast.error(formatApiError(err.response?.data?.detail));
        navigate("/");
      });
    return () => { ignored = true; };
  }, [id, navigate, setNodes, setEdges]);

  const updateLabel = useCallback((nodeId, label) => {
    snapshot();
    setNodes((nds) =>
      nds.map((n) => {
        if (n.id !== nodeId) return n;
        const history = [...(n.data.history || []), { at: nowIso(), from: n.data.label, to: label }];
        return { ...n, data: { ...n.data, label, updatedAt: nowIso(), history } };
      })
    );
  }, [snapshot, setNodes]);

  const addChild = useCallback((parentId) => {
    snapshot();
    const cid = newId();
    setNodes((nds) => {
      const parent = nds.find((n) => n.id === parentId);
      const px = parent ? parent.position.x : 0;
      const py = parent ? parent.position.y : 0;
      const siblings = nds.filter((n) => n._parent === parentId).length;
      return [...nds, {
        id: cid, type: "mindnode", _parent: parentId,
        position: { x: px + 300, y: py + siblings * 120 },
        data: { label: "Nueva rama", createdAt: nowIso(), updatedAt: nowIso(), history: [], isRoot: false },
      }];
    });
    setEdges((eds) => [...eds, { id: `e${parentId}-${cid}`, source: parentId, target: cid, type: "smoothstep", style: edgeStyle, markerEnd: marker }]);
  }, [snapshot, setNodes, setEdges]);

  const removeNode = useCallback((nodeId) => {
    if (nodeId === "root") return;
    snapshot();
    const toRemove = new Set([nodeId]);
    let changed = true;
    while (changed) {
      changed = false;
      for (const e of edges) {
        if (toRemove.has(e.source) && !toRemove.has(e.target)) {
          toRemove.add(e.target);
          changed = true;
        }
      }
    }
    setNodes((nds) => nds.filter((n) => !toRemove.has(n.id)));
    setEdges((eds) => eds.filter((e) => !toRemove.has(e.source) && !toRemove.has(e.target)));
  }, [edges, snapshot, setNodes, setEdges]);

  const openHistory = useCallback((nodeId) => setHistoryFor(nodeId), []);

  const setEmoji = useCallback((nodeId, emoji) => {
    snapshot();
    setNodes((nds) => nds.map((n) => (n.id !== nodeId ? n : { ...n, data: { ...n.data, emoji: emoji || undefined, updatedAt: nowIso() } })));
  }, [snapshot, setNodes]);

  const setShape = useCallback((nodeId, shape) => {
    snapshot();
    setNodes((nds) => nds.map((n) => (n.id !== nodeId ? n : { ...n, data: { ...n.data, shape, updatedAt: nowIso() } })));
  }, [snapshot, setNodes]);

  const toggleCollapse = useCallback((nodeId) => {
    snapshot();
    setNodes((nds) => nds.map((n) => (n.id !== nodeId ? n : { ...n, data: { ...n.data, collapsed: !n.data?.collapsed } })));
  }, [snapshot, setNodes]);

  const LINK_ROUTE = { note: "note", sheet: "hoja", movemap: "movimientos" };
  const linkFormat = useCallback(async (nodeId, type) => {
    const cfg = {
      note: { type: "text", title: "Nota vinculada", extra: { content: "" } },
      sheet: { type: "sheet", title: "Hoja vinculada", extra: { sheet: { cells: emptyCells(12, 6) } } },
      movemap: { type: "movemap", title: "Movimientos vinculados", extra: { movemap: { displayCurrency: "USD", rate: 0, movements: [] } } },
    }[type];
    if (!cfg) return;
    try {
      const { data } = await api.post("/notes", { type: cfg.type, title: cfg.title, ...cfg.extra });
      setNodes((nds) => nds.map((n) => (n.id !== nodeId ? n : { ...n, data: { ...n.data, link: { type, id: data.id, title: cfg.title }, updatedAt: nowIso() } })));
      const win = window.open(`/${LINK_ROUTE[type]}/${data.id}`, "_blank");
      if (win) toast.success("Formato vinculado y abierto en otra pestaña. Recuerda guardar el mapa.");
      else toast.success("Formato vinculado. Pulsa el chip del nodo para abrirlo. Recuerda guardar el mapa.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  }, [setNodes]);

  const openLink = useCallback((nodeId) => {
    const n = nodes.find((x) => x.id === nodeId);
    const l = n?.data?.link;
    if (l) window.open(`/${LINK_ROUTE[l.type]}/${l.id}`, "_blank");
  }, [nodes]);

  const removeLink = useCallback((nodeId) => {
    snapshot();
    setNodes((nds) => nds.map((n) => (n.id !== nodeId ? n : { ...n, data: { ...n.data, link: undefined, updatedAt: nowIso() } })));
  }, [snapshot, setNodes]);

  const [exporting, setExporting] = useState(false);
  const capturePng = useCallback(async () => {
    const el = document.querySelector(".react-flow__viewport");
    const bounds = getRectOfNodes(nodes);
    const w = Math.max(Math.round(bounds.width) + 200, 800);
    const h = Math.max(Math.round(bounds.height) + 200, 600);
    const [tx, ty, tzoom] = getTransformForBounds(bounds, w, h, 0.3, 2, 0.15);
    const bg = theme === "dark" ? "#020617" : "#ffffff";
    return toPng(el, {
      backgroundColor: bg,
      width: w,
      height: h,
      pixelRatio: 2,
      style: { width: `${w}px`, height: `${h}px`, transform: `translate(${tx}px, ${ty}px) scale(${tzoom})` },
    });
  }, [nodes, theme]);

  const sharePng = useCallback(async () => {
    if (!nodes.length) {
      toast.error("El mapa está vacío.");
      return;
    }
    setExporting(true);
    try {
      const dataUrl = await capturePng();
      const fileName = `${(title || "mapa-mental").replace(/[^\w\-]+/g, "_")}.png`;
      const blob = await (await fetch(dataUrl)).blob();
      const file = new File([blob], fileName, { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({ files: [file], title: title || "Mapa mental", text: `Mapa mental: ${title || ""}` });
          return;
        } catch (err) {
          if (err.name === "AbortError") return; // user cancelled the share sheet
        }
      }
      const a = document.createElement("a");
      a.href = dataUrl;
      a.download = fileName;
      a.click();
      toast.success("Imagen PNG descargada");
    } catch (err) {
      toast.error("No se pudo generar la imagen.");
    } finally {
      setExporting(false);
    }
  }, [nodes, title, capturePng]);

  const childrenMap = useMemo(() => {
    const m = {};
    for (const e of edges) (m[e.source] ||= []).push(e.target);
    return m;
  }, [edges]);
  const descendantsOf = useCallback((nid) => {
    const out = []; const stack = [...(childrenMap[nid] || [])];
    while (stack.length) { const c = stack.pop(); out.push(c); for (const g of (childrenMap[c] || [])) stack.push(g); }
    return out;
  }, [childrenMap]);
  const { displayNodes, displayEdges } = useMemo(() => {
    const hidden = new Set();
    for (const n of nodes) if (n.data?.collapsed) for (const d of descendantsOf(n.id)) hidden.add(d);
    const dn = nodes.map((n) => ({
      ...n,
      hidden: hidden.has(n.id),
      data: { ...n.data, _hasChildren: (childrenMap[n.id] || []).length > 0, _collapsed: !!n.data?.collapsed, _hiddenCount: n.data?.collapsed ? descendantsOf(n.id).length : 0 },
    }));
    const de = edges.map((e) => ({ ...e, hidden: hidden.has(e.source) || hidden.has(e.target) }));
    return { displayNodes: dn, displayEdges: de };
  }, [nodes, edges, childrenMap, descendantsOf]);

  const ctxValue = useMemo(() => ({ updateLabel, addChild, removeNode, openHistory, setEmoji, setShape, kids, linkFormat, openLink, removeLink, toggleCollapse }), [updateLabel, addChild, removeNode, openHistory, setEmoji, setShape, kids, linkFormat, openLink, removeLink, toggleCollapse]);

  const save = useCallback(async () => {
    setSaving(true);
    let finalTitle = title;
    const t = (title || "").trim();
    if (!t || t === "Nuevo mapa mental" || t === "Sin título") {
      const root = nodes.find((n) => n.data?.isRoot) || nodes.find((n) => n.id === "root") || nodes[0];
      const rl = (root?.data?.label || "").trim();
      if (rl && rl !== "Idea central") {
        finalTitle = rl.slice(0, 80);
        setTitle(finalTitle);
      }
    }
    try {
      const cleanNodes = nodes.map(({ id, type, position, data, _parent }) => ({ id, type, position, _parent: _parent || null, data }));
      await api.put(`/notes/${id}`, { title: finalTitle, mindmap: { nodes: cleanNodes, edges, kids } });
      toast.success("Mapa guardado");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  }, [id, title, nodes, edges, kids]);

  const changeFolder = async (v) => {
    setFolderId(v);
    try {
      await api.put(`/notes/${id}`, { folder_id: v });
      toast.success(v ? "Movido a la carpeta" : "Quitado de la carpeta");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const saveToDocuments = async () => {
    if (!nodes.length) {
      toast.error("El mapa está vacío.");
      return;
    }
    setExporting(true);
    try {
      const dataUrl = await capturePng();
      const image_base64 = dataUrl.split(",")[1];
      await api.post("/docs/create-pdf", { title: title || "Mapa mental", image_base64, folder_id: folderId || "", source: "mindmap" });
      toast.success("Mapa guardado como PDF (imagen) en Documentos");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setExporting(false);
    }
  };

  const convertToNote = async () => {
    const content = mindmapToMarkdown(title, nodes, edges);
    try {
      const { data } = await api.post("/notes", { type: "text", title: `${title || "Mapa"} (nota)`, content, folder_id: folderId || null });
      toast.success("Nota creada desde tu mapa");
      navigate(`/note/${data.id}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const historyNode = nodes.find((n) => n.id === historyFor);
  const bgColor = theme === "dark" ? "#1e293b" : "#e2e8f0";

  return (
    <div className={`h-screen flex flex-col ${PAGE} font-body`}>
      <Navbar />
      <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-200 dark:border-slate-800 flex-wrap">
        <button onClick={() => navigate("/")} className={ICON_BTN} data-testid="back-button"><ArrowLeft size={18} /></button>
        <FolderSelect value={folderId} onChange={changeFolder} />
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="flex-1 min-w-[120px] bg-transparent font-heading font-normal text-xl tracking-tight focus:outline-none"
          data-testid="mindmap-title-input"
        />
        <div className="flex items-center gap-2">
          <button onClick={undo} disabled={!canUndo} className={`${ICON_BTN} disabled:opacity-40`} data-testid="mindmap-undo" title="Deshacer (Ctrl+Z)"><Undo2 size={18} /></button>
          <button onClick={redo} disabled={!canRedo} className={`${ICON_BTN} disabled:opacity-40`} data-testid="mindmap-redo" title="Rehacer (Ctrl+Y)"><Redo2 size={18} /></button>
          <button onClick={() => zoomIn()} className={ICON_BTN} data-testid="zoom-in"><ZoomIn size={18} /></button>
          <button onClick={() => zoomOut()} className={ICON_BTN} data-testid="zoom-out"><ZoomOut size={18} /></button>
          <button onClick={() => fitView({ padding: 0.2 })} className={ICON_BTN} data-testid="fit-view"><Maximize size={18} /></button>
          <button onClick={() => setKids((v) => !v)} className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-lg border transition-all ${kids ? "bg-pink-500 border-pink-500 text-white" : "border-slate-200 dark:border-slate-800 text-slate-600 dark:text-slate-300"}`} data-testid="kids-mode-toggle" title="Modo Niños: nodos grandes y coloridos">
            <Baby size={15} /> <span className="hidden sm:inline">Modo Niños</span>
          </button>
          <button onClick={sharePng} className={ICON_BTN} disabled={exporting} data-testid="share-png" title="Compartir como imagen PNG">
            <Share2 size={18} />
          </button>
          <button onClick={saveToDocuments} className={ICON_BTN} data-testid="save-pdf-documents" title="Guardar como PDF en Documentos">
            <FilePlus size={18} />
          </button>
          <button onClick={convertToNote} className={ICON_BTN} data-testid="convert-to-note" title="Convertir este mapa en una nota de texto">
            <FileText size={18} />
          </button>
          <button onClick={() => setImproveOpen(true)} className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-lg bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-white transition-all" data-testid="improve-ai-button" title="Mejora este mapa con IA">
            <Sparkles size={15} /> <span className="hidden sm:inline">Mejorar con IA</span>
          </button>
          <ShareButton targetType="note" targetId={id} />
          <button onClick={save} className={BTN_PRIMARY} disabled={saving} data-testid="save-mindmap">
            <Save size={15} /> {saving ? "..." : "Guardar"}
          </button>
        </div>
      </div>

      <div className="flex-1 relative">
        <MindMapContext.Provider value={ctxValue}>
          <ReactFlow
            nodes={displayNodes}
            edges={displayEdges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onNodeDragStart={() => snapshot()}
            nodeTypes={nodeTypes}
            fitView
            minZoom={0.2}
            maxZoom={3}
            proOptions={{ hideAttribution: true }}
          >
            <Background gap={22} size={1.5} color={bgColor} />
            <Controls showInteractive={false} />
          </ReactFlow>
        </MindMapContext.Provider>

        {historyNode && (
          <div className="absolute top-4 right-4 w-72 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl shadow-xl p-4 z-10" data-testid="history-panel">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-heading font-medium text-sm">Historial de la rama</h3>
              <button onClick={() => setHistoryFor(null)} className={ICON_BTN}><X size={14} /></button>
            </div>
            <p className="font-mono2 text-[11px] text-slate-500 dark:text-slate-400 mb-1">
              Creada: {historyNode.data.createdAt ? format(new Date(historyNode.data.createdAt), "d 'de' MMMM yyyy · HH:mm", { locale: es }) : "-"}
            </p>
            <p className="font-mono2 text-[11px] text-slate-500 dark:text-slate-400 mb-3">
              Modificada: {historyNode.data.updatedAt ? format(new Date(historyNode.data.updatedAt), "dd/MM/yyyy HH:mm") : "-"}
            </p>
            <div className="max-h-48 overflow-y-auto flex flex-col gap-2.5">
              {(historyNode.data.history || []).length === 0 ? (
                <p className="font-mono2 text-[11px] text-slate-400">Sin cambios registrados.</p>
              ) : (
                [...historyNode.data.history].reverse().map((h, i) => (
                  <div key={`${h.at}-${i}`} className="border-l-2 border-indigo-400 pl-2.5">
                    <p className="font-mono2 text-[10px] text-slate-400">{format(new Date(h.at), "d MMM yyyy · HH:mm", { locale: es })}</p>
                    <p className="font-body text-xs">"{h.from}" → "{h.to}"</p>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>
      <AiImproveModal
        open={improveOpen}
        mode="mindmap"
        getText={() => mindmapToMarkdown(title, nodes, edges)}
        onApply={({ title: t, outline }) => {
          const mm = outlineToMindmap(title || t, outline);
          setNodes(mm.nodes);
          setEdges(mm.edges);
          toast.info("Mapa actualizado. Revisa y guarda los cambios.");
        }}
        onClose={() => setImproveOpen(false)}
      />
    </div>
  );
}

export default function MindMapEditor() {
  const { id } = useParams();
  return (
    <ReactFlowProvider>
      <Editor id={id} />
    </ReactFlowProvider>
  );
}
